/**
 * Phase 3.5: tiny helper that reads the current `acting_as` selection from
 * localStorage (written by ActingAsContext) and appends `?acting_as=<id>`
 * to URL query strings.
 *
 * Two variants:
 *   - withActingAs — used by the default-mode View-as surfaces: schedule,
 *     positions, orders, analytics. Backend accepts SuperAdmin / Admin /
 *     AM-with-relation on GETs.
 *   - withActingAsIfSuperAdmin — used by the restricted-mode surfaces:
 *     basket, strategy, allocation, basket-export. Backend enforces
 *     super_admin_only=True on every method (GET reads AND POST/PUT/DELETE
 *     mutations), so this variant only appends the param for SuperAdmin
 *     callers — Admin/AM see their own data silently rather than hitting 403.
 *
 * Reading from localStorage (vs context) keeps services framework-agnostic
 * and avoids prop-drilling through every call site. The provider keeps both
 * in sync on the same tab.
 */
const STORAGE_KEY = 'acting_as';

export function getActingAs(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Mutates the given URLSearchParams to include `acting_as` when set. Returns
 *  the same instance for chaining. */
export function withActingAs(qp: URLSearchParams): URLSearchParams {
  const v = getActingAs();
  if (v) qp.set('acting_as', v);
  return qp;
}

/** True when the currently-logged-in user is in the SuperAdmins Cognito group.
 *  Reads from the `user_profile` cache that authService populates on login. */
export function isCurrentUserSuperAdmin(): boolean {
  try {
    const raw = localStorage.getItem('user_profile');
    if (!raw) return false;
    const profile = JSON.parse(raw);
    const groups: string[] = Array.isArray(profile?.groups) ? profile.groups : [];
    return groups.includes('SuperAdmins');
  } catch {
    return false;
  }
}

/** SuperAdmin-only variant of withActingAs — for endpoints whose backend
 *  enforces `super_admin_only=True` (baskets / strategies / allocations /
 *  basket-export). Non-SuperAdmin callers send no acting_as param, so the
 *  backend returns their own data instead of a 403. */
export function withActingAsIfSuperAdmin(qp: URLSearchParams): URLSearchParams {
  if (isCurrentUserSuperAdmin()) return withActingAs(qp);
  return qp;
}
